import { useState, useCallback } from "react";
import { Button, Flex, Text } from "@radix-ui/themes";
import { Dialog } from "@/components/shared/dialog";
import { SubTopic } from "@/v2/entities/subtopic/SubTopic.model";
import { useSubTopicApi } from "@/v2/entities/subtopic/SubTopic.api";
import { Loader2Icon } from "lucide-react";
import { toast } from "sonner";

interface DeleteSubTopicDialogProps {
  subTopic: SubTopic;
  onSuccess: () => void;
  onCancel: () => void;
}

export function DeleteSubTopicDialog({
  subTopic,
  onSuccess,
  onCancel,
}: DeleteSubTopicDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const subTopicApi = useSubTopicApi();

  const handleDelete = useCallback(async () => {
    setIsLoading(true);
    try {
      await subTopicApi.deleteSubTopic(subTopic.id);
      toast.success("SubTopic deleted successfully");
      onSuccess();
    } catch {
      toast.error("Failed to delete subtopic");
    } finally {
      setIsLoading(false);
    }
  }, [subTopic.id, subTopicApi, onSuccess]);

  return (
    <Dialog.Content size="small">
      <Flex direction="column" gap="4" p="4">
        <Dialog.Title>Delete SubTopic</Dialog.Title>
        <Text size="2" color="gray">
          Are you sure you want to delete &quot;{subTopic.name}&quot;? This will
          remove it from all tickets and conversations.
        </Text>
        <Flex gap="3" justify="end">
          <Button
            variant="soft"
            color="gray"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            color="red"
            onClick={handleDelete}
            disabled={isLoading}
            highContrast
          >
            {isLoading ? <Loader2Icon className="animate-spin" /> : "Delete"}
          </Button>
        </Flex>
      </Flex>
    </Dialog.Content>
  );
}

